import type { MetricIndicatorState } from '@/components/common/metric-indicator';
import { formatLastSync } from '@/lib/date';

import { DEVICE_STATUS, type DeviceStatus } from './constants';
import { SUBSCRIPTION_INDICATOR, type SubscriptionIndicator } from './details-schemas';
import type { DeviceMapPopup, TelemetryUpdate } from './map-popup-schemas';
import type { MapDeviceStatus } from './map-schemas';

/**
 * The one `MapDeviceStatus` token the Devices-listing chip table has no entry for — named rather
 * than compared as a raw string, per `quickViewChipStatus`'s fallback below. `satisfies`, not a
 * `MapDeviceStatus` annotation, so the literal type survives for the equality-narrowing below —
 * annotating it would widen `status` back to the full union in the fallback branch.
 */
const PENDING_CONFIRMATION = 'pending_confirmation' satisfies MapDeviceStatus;

/**
 * The three named slots, in the order §6a lists them. The keys are the payload's own
 * (`metrics.display.*` / `metrics.indicators.*`), so a slot cannot read one metric's value beside
 * another's indicator.
 */
export const METRIC_SLOTS = [
  { key: 'batteryVoltage', label: 'Battery Voltage' },
  { key: 'temperature', label: 'Temperature' },
  { key: 'waterLevel', label: 'Water Level' },
] as const;

export type MetricSlotKey = (typeof METRIC_SLOTS)[number]['key'];

/** Shown for a metric with no reading (§6d) — an em dash, never a `0`, which would read as a
 *  measurement. The unit is dropped with it: `"— V"` reads as an unknown number of volts. */
export const METRIC_DASH = '—';

/** §8c: a Device that has never reported reads as never-synced, not blank and not elapsed. */
export const NEVER_SYNCED = 'Never synced';

export type QuickViewIdentity = { primary: string; secondary: string | null };

/**
 * The two identity lines (§4b–§4c).
 *
 * The same rule the Device Details page's header applies, and for the same reason: real accounts do
 * set `customName` to the identical string as the device's own name, and §4c forbids repeating it.
 * Trimmed on both sides, so trailing whitespace is not the difference that prints a name twice.
 */
export function quickViewIdentity(names: {
  customName: string | null;
  deviceName: string;
}): QuickViewIdentity {
  const custom = names.customName?.trim() ?? '';
  const device = names.deviceName.trim();

  if (custom === '' || custom === device) return { primary: device, secondary: null };
  return { primary: custom, secondary: device };
}

/**
 * Which `<Chip>` the status pill draws (§5a), taken from the Device Details page's own table
 * (`DEVICE_STATUS_CHIP`) rather than a second mapping — §17b's "the two agree exactly", held by there
 * being one table.
 *
 * An ended subscription folds to **Expired** and hides the live status, which is what the page does
 * (US-112437 §19). `pending_confirmation` is a marker-layer status the listing has no chip for; it
 * degrades to Pending Configuration, the same conservative fallback `map-schemas.ts` uses.
 */
export function quickViewChipStatus(
  status: MapDeviceStatus,
  subscription: SubscriptionIndicator,
): DeviceStatus {
  if (subscription === SUBSCRIPTION_INDICATOR.EXPIRED) return DEVICE_STATUS.EXPIRED;
  return status === PENDING_CONFIRMATION ? DEVICE_STATUS.PENDING_CONFIGURATION : status;
}

/**
 * Whether the active-subscription badge is shown (§5c, §5d).
 *
 * **Both** active states qualify: §5c turns on the subscription being active, and a subscription
 * expiring soon has not ended. The Device Details page draws an interactive info icon for
 * `expiring_soon` instead — an affordance the read-only panel may not carry (§12b) and whose
 * destination (the metadata card) does not exist here. Recorded as a deviation.
 */
export function showsSubscriptionBadge(subscription: SubscriptionIndicator): boolean {
  return (
    subscription === SUBSCRIPTION_INDICATOR.PROTECTED ||
    subscription === SUBSCRIPTION_INDICATOR.EXPIRING_SOON
  );
}

/**
 * Whether a metric shows an indicator, and which (§7a–§7d).
 *
 * Three refusals and one pass-through, and nothing thresholded: the state is the backend's
 * (§17a/§7b). No indicator while Disconnected — the status pill and the last-sync line carry that the
 * readings are not current (§7c) — and none for a dashed metric, which has no reading to qualify
 * (§7d).
 */
export function metricIndicatorFor(
  state: MetricIndicatorState | null,
  display: string | null,
  isDisconnected: boolean,
): MetricIndicatorState | null {
  if (isDisconnected || display === null) return null;
  return state;
}

/**
 * The last-sync line (§8a, §8c).
 *
 * `formatLastSync` is the **Device Details page's own** formatter (`@/lib/date`, US-112437 §1b /
 * US-112439 §4), so §17b holds by the two calling one function. It answers `null` for a Device that
 * has never reported; the page omits the line entirely there, but §8c requires the panel to say so.
 *
 * `now` is a parameter rather than a `Date.now()` read, so the wording is assertable arithmetic.
 */
export function lastSyncLine(lastSyncedAt: string | null, now?: Date): string {
  const elapsed = formatLastSync(lastSyncedAt, now);
  return elapsed === null ? NEVER_SYNCED : `Last Sync ${elapsed}`;
}

/**
 * Merge one `telemetry` event into the Device's current quick-view payload (113588 §9a).
 *
 * **Absent keys are preserved, not cleared.** That single rule is what §9d asks for — an
 * interruption, a partial event, or a channel that never speaks all leave the displayed values
 * frozen at the last received readings rather than blanking them. A key the event *does* carry wins,
 * `null` included, because `null` is a real reading-less state and renders the dash (§6d).
 *
 * Nothing here clears `metrics.display` on a status change, which is §10a: a Device going
 * Disconnected keeps its last known values and merely stops showing indicators — a refusal that
 * already lives in `metricIndicatorFor`, so this function does not repeat it.
 *
 * A fresh object every time, never a mutation: TanStack Query compares by reference, and a mutated
 * payload would leave the panel showing stale text.
 */
export function applyTelemetry(current: DeviceMapPopup, update: TelemetryUpdate): DeviceMapPopup {
  const { metrics, ...fields } = update;

  return {
    ...current,
    ...fields,
    metrics: metrics
      ? {
          display: { ...current.metrics.display, ...metrics.display },
          indicators: { ...current.metrics.indicators, ...metrics.indicators },
        }
      : current.metrics,
  };
}

/** The two things §5f requires be announced when they change while the panel is open. */
export type QuickViewLiveState = { statusLabel: string; hasBadge: boolean };

/**
 * What a screen reader should hear when the status pill or the subscription badge changes in place
 * (113588 §5f) — and, just as load-bearing, **when it should hear nothing**.
 *
 * `null` for the first reading: the panel announces its own content on open (§13c), and repeating
 * the status there would be the second announcement §5f is trying to prevent, not the first.
 * `null` when nothing moved, which is what keeps §9e's "no notification of either the interruption
 * or the recovery" true — a value refresh is not an event worth speaking.
 *
 * Wording is FE-drafted; no criterion supplies copy, and Figma covers no announcement (M4).
 */
export function liveAnnouncement(
  previous: QuickViewLiveState | null,
  next: QuickViewLiveState,
): string | null {
  if (previous === null) return null;

  const parts: string[] = [];
  if (previous.statusLabel !== next.statusLabel) parts.push(`Status ${next.statusLabel}`);
  if (previous.hasBadge !== next.hasBadge) {
    parts.push(next.hasBadge ? 'Subscription active' : 'Subscription ended');
  }

  return parts.length === 0 ? null : parts.join('. ');
}

export type ReadingSplit = { reading: string; unit: string | null };

/**
 * Split a formatted reading into its reading and its unit, for TYPOGRAPHY ONLY (QA round 2 D5a,
 * Figma `864:558014`): 24px for the reading, 14px for the unit beside it. The API keeps sending
 * `"2.7 V"` and this panel keeps showing exactly those characters — nothing here recomputes a value,
 * chooses a unit, or changes precision, which is the §17a constraint this deliberately does not
 * revisit.
 *
 * Splits on the LAST run of whitespace, not the first plain space: a reading that itself contains a
 * space (unlikely today, but not this function's business to assume) must not be cut in the middle.
 * No whitespace at all — a bare number, or the em-dash placeholder — returns the whole string as the
 * reading with no unit, rather than inventing one.
 */
export function splitReading(display: string): ReadingSplit {
  const isWhitespace = (char: string) => /\s/.test(char);

  let i = display.length;
  // The trailing token: walk back over its own non-whitespace run.
  while (i > 0 && !isWhitespace(display[i - 1]!)) i -= 1;
  const unitStart = i;
  // The whitespace run separating it from the reading.
  while (i > 0 && isWhitespace(display[i - 1]!)) i -= 1;
  const readingEnd = i;

  if (readingEnd === unitStart) return { reading: display, unit: null };
  return { reading: display.slice(0, readingEnd), unit: display.slice(unitStart) };
}

/**
 * Three dashed metrics — what an offline panel with nothing cached shows (113588 §15b).
 *
 * §15b was marked *Requires confirmation* and took its own recommendation: the dash placeholder plus
 * the offline indicator, **not** an error. A Grower's first visit in a browser has no cache to fall
 * back on, and an error there would be a lie about the Device rather than about the connection.
 */
export const OFFLINE_METRICS: DeviceMapPopup['metrics'] = {
  display: { batteryVoltage: null, temperature: null, waterLevel: null },
  indicators: { batteryVoltage: null, temperature: null, waterLevel: null },
};
